import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { useU2000ExportExcelNotification } from "../../../u2000Core/hooks/handler/U2000UseExportExcelNotification";
import { useU2000LogicalUnitContext } from "../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { extractBaseLanguage } from "../../../u2000Core/tools/U2000LanguageUtils";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { NS } from "../../U3125";
import {
  doExportArAgingExcel,
  doExportArAgingPdf,
  luHandleAppiError,
  u3125BuildArAgingRequestBody,
  u3125FilterArAgingCustomerDetailByBucket,
  u3125FilterArAgingSummaryByBucket,
  u3125ForfaitWizardTodayIsoDate,
} from "../../U3125Service";
import { useU3125ArAgingExportHandlers } from "../../U3125UseArAgingExportHandlers";
import {
  u3125ClearArAgingFullReportFallbackCache,
  useU3125ArAgingSummaryHandlers,
} from "../../U3125UseArAgingSummaryHandlers";
import {
  u3125ClearArAgingCustomerDetailCache,
  useU3125ArAgingCustomerDetailHandlers,
} from "../../U3125UseArAgingCustomerDetailHandlers";
import type { U2000AgingBucket } from "../../../u2000Core/constants/U2000AgingBuckets";
import U3125ArAgingBucketTable from "./U3125ArAgingBucketTable";
import U3125ArAgingHierarchySection from "./U3125ArAgingHierarchySection";
import U3125ArAgingReportToolbar from "./U3125ArAgingReportToolbar";
import U3125ForfaitWizardInvoiceDateInput from "../wizard/U3125ForfaitWizardInvoiceDateInput";
import U3125ArAgingInlineSpinner from "./U3125ArAgingInlineSpinner";

const AS_OF_DEBOUNCE_MS = 300;

const U3125ArAgingReportPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const axios = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const { summary, isLoading: isSummaryLoading, loadSummary } = useU3125ArAgingSummaryHandlers();
  const {
    customerDetail,
    loadingCustomerId,
    loadCustomerDetail,
    clearCustomerDetail,
  } = useU3125ArAgingCustomerDetailHandlers();
  const [asOfDate, setAsOfDate] = useState(() => u3125ForfaitWizardTodayIsoDate());
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedBucket, setSelectedBucket] = useState<U2000AgingBucket | null>(null);
  const [expandedCustomerId, setExpandedCustomerId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstLoadRef = useRef(true);

  useU2000ExportExcelNotification();

  useEffect(() => {
    setLogicalUnitBreadcrumb([
      { label: t("title", { ns: NS }), href: "/U3125" },
      { label: t("arAgingBreadcrumb", { ns: NS }) },
    ]);
    return () => setLogicalUnitBreadcrumb([]);
  }, [setLogicalUnitBreadcrumb, t]);

  const handleApiError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast],
  );

  const { exportExcel, exportPdf, isExportingExcel, isExportingPdf } = useU3125ArAgingExportHandlers({
    doExportArAgingExcelFn: (body) => doExportArAgingExcel(axios, body),
    doExportArAgingPdfFn: (body) => doExportArAgingPdf(axios, body),
    onExcelSuccess: () => toast.info(t("msgArAgingReportGenerationStarted", { ns: NS })),
    onPdfSuccess: () => toast.info(t("msgArAgingReportGenerationStarted", { ns: NS })),
    onError: ({ error }) => handleApiError(error),
  });

  const resetCustomerExpansion = useCallback(() => {
    setExpandedCustomerId(null);
    setSelectedProjectId(null);
    clearCustomerDetail();
    u3125ClearArAgingCustomerDetailCache();
  }, [clearCustomerDetail]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const delay = isFirstLoadRef.current ? 0 : AS_OF_DEBOUNCE_MS;
    isFirstLoadRef.current = false;
    debounceRef.current = setTimeout(() => {
      resetCustomerExpansion();
      setSelectedBucket(null);
      u3125ClearArAgingFullReportFallbackCache();
      void loadSummary(asOfDate);
    }, delay);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [asOfDate, loadSummary, resetCustomerExpansion]);

  useEffect(() => {
    if (selectedBucket != null) {
      resetCustomerExpansion();
    }
  }, [selectedBucket, resetCustomerExpansion]);

  const handleSelectBucket = useCallback((bucket: U2000AgingBucket | null) => {
    setSelectedBucket(bucket);
  }, []);

  const filteredCustomers = useMemo(() => {
    if (!summary) return [];
    if (!selectedBucket) return summary.customers;
    return u3125FilterArAgingSummaryByBucket(summary.customers, selectedBucket);
  }, [summary, selectedBucket]);

  const displayCustomerDetail = useMemo(() => {
    if (!customerDetail || customerDetail.customer.id !== expandedCustomerId) return null;
    if (!selectedBucket) return customerDetail;
    return u3125FilterArAgingCustomerDetailByBucket(customerDetail, selectedBucket);
  }, [customerDetail, expandedCustomerId, selectedBucket]);

  const handleToggleCustomer = useCallback(
    (customerId: string) => {
      if (expandedCustomerId === customerId) {
        resetCustomerExpansion();
        return;
      }
      setExpandedCustomerId(customerId);
      setSelectedProjectId(null);
      clearCustomerDetail();
      void loadCustomerDetail(asOfDate, customerId);
    },
    [asOfDate, clearCustomerDetail, expandedCustomerId, loadCustomerDetail, resetCustomerExpansion],
  );

  const exportBody = useMemo(
    () =>
      u3125BuildArAgingRequestBody({
        asOfDate,
        lang: extractBaseLanguage(i18n.language),
        bucket: selectedBucket,
        projectId: selectedProjectId,
      }),
    [asOfDate, i18n.language, selectedBucket, selectedProjectId],
  );

  const handleExportExcel = useCallback(() => {
    void exportExcel(exportBody);
  }, [exportExcel, exportBody]);

  const handleExportPdf = useCallback(() => {
    void exportPdf(exportBody);
  }, [exportPdf, exportBody]);

  const hasReportData = summary != null;
  const isEmptyReport =
    hasReportData && summary.customers.length === 0 && summary.organization.totals.invoiceCount === 0;
  const isEmptyBucketFilter = hasReportData && selectedBucket != null && filteredCustomers.length === 0;

  const selectedBucketLabel =
    selectedBucket != null
      ? t(`arAging.bucket.${selectedBucket}`, { ns: NS, defaultValue: selectedBucket })
      : "";

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{t("titleArAgingReport", { ns: NS })}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("lblArAgingAsOf", { ns: NS, date: asOfDate })}</p>
        </div>
        <U3125ArAgingReportToolbar
          onExportExcel={handleExportExcel}
          onExportPdf={handleExportPdf}
          exportDisabled={!hasReportData || isSummaryLoading}
          isExportingExcel={isExportingExcel}
          isExportingPdf={isExportingPdf}
        />
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <U3125ForfaitWizardInvoiceDateInput
          value={asOfDate}
          onChange={setAsOfDate}
          label={t("lblArAgingAsOfDate", { ns: NS })}
          disabled={isSummaryLoading}
        />
        {isSummaryLoading ? (
          <span className="flex items-center gap-2 pb-2 text-sm text-muted-foreground">
            <span>{t("msgArAgingLoading", { ns: NS })}</span>
            <U3125ArAgingInlineSpinner />
          </span>
        ) : null}
      </div>

      {isEmptyReport ? (
        <p className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-muted-foreground">
          {t("msgArAgingNoOutstandingInvoices", { ns: NS })}
        </p>
      ) : null}

      {hasReportData && !isEmptyReport ? (
        <>
          {selectedBucket ? (
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm">
              <span>{t("lblArAgingFilterActiveBucket", { ns: NS, bucket: selectedBucketLabel })}</span>
              <button
                type="button"
                className="text-primary-700 underline hover:no-underline"
                onClick={() => handleSelectBucket(null)}
              >
                {t("btnArAgingClearBucketFilter", { ns: NS })}
              </button>
            </div>
          ) : null}

          <section className="space-y-2 rounded-md border border-gray-200 bg-white p-4">
            <h2 className="text-sm font-semibold">{t("lblArAgingOrganization", { ns: NS })}</h2>
            <U3125ArAgingBucketTable
              buckets={summary.organization.buckets}
              totals={summary.organization.totals}
              selectedBucket={selectedBucket}
              linkableBuckets
              onSelectBucket={handleSelectBucket}
            />
          </section>

          {isEmptyBucketFilter ? (
            <p className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-muted-foreground">
              {t("msgArAgingNoDataForBucket", { ns: NS, bucket: selectedBucketLabel })}
            </p>
          ) : (
            <>
              <section className="rounded-md border border-gray-200 bg-white p-4">
                <U3125ArAgingHierarchySection
                  customers={filteredCustomers}
                  expandedCustomerId={expandedCustomerId}
                  loadingCustomerId={loadingCustomerId}
                  customerDetail={displayCustomerDetail}
                  selectedProjectId={selectedProjectId}
                  onToggleCustomer={handleToggleCustomer}
                  onSelectProject={setSelectedProjectId}
                  selectedBucket={selectedBucket}
                />
              </section>
            </>
          )}
        </>
      ) : null}
    </div>
  );
};

export default U3125ArAgingReportPage;
